/**
 * Canonical JSON for outbox payload hashing (docs/08 §4).
 * Keys sorted; server-owned created_at/updated_at excluded; numbers as integers;
 * nulls preserved; bigint → integer number.
 */

const SERVER_OWNED = new Set(['created_at', 'updated_at']);

export function canonicalize(value: unknown): unknown {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return value;
  }
  if (typeof value === 'bigint') {
    if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new Error('canonicalize: bigint outside safe integer range');
    }
    return Number(value);
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
      throw new Error('canonicalize: non-integer number');
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => canonicalize(item));
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => !SERVER_OWNED.has(k))
      .sort();
    const out: Record<string, unknown> = {};
    for (const k of keys) {
      out[k] = canonicalize(obj[k]);
    }
    return out;
  }
  throw new Error('canonicalize: unsupported type');
}

/** Stable JSON string used for hashing and for the flush payload body. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

/** SHA-256 hex of the canonical JSON (docs/08 §4 idempotent retry). */
export async function payloadHash(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const digest: ArrayBuffer = await crypto.subtle.digest('SHA-256', bytes);
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  return hex;
}
